// pages/ContactPage.tsx - Contact page for Potkurikorjaus
// Sends to PHP backend -> info@potkurikorjaus.fi
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageSquare, AlertCircle, Send, CheckCircle, Calculator, Camera, X, CalendarOff } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSiteConfig, getUpcomingHolidays, isHolidayActive } from '../hooks/useSiteConfig';

// Anti-spam configuration
const MIN_FORM_TIME_SECONDS = 5; // Minimum time to fill form (bots are fast)
const RATE_LIMIT_MS = 60000; // 1 minute between submissions

// Map urgency types to priority codes
const urgencyToPriority: Record<string, string> = {
  'Normaali': 'green',
  'Kiireellinen': 'yellow',
  'Erittäin kiireellinen': 'orange',
  'Hätätyö': 'red'
};

const ContactPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const { config } = useSiteConfig();

  // Get upcoming holidays
  const upcomingHolidays = getUpcomingHolidays(config?.holidays || [], 30);
  const activeHoliday = config?.holidays?.find(h => isHolidayActive(h));

  // Get URL parameters from pricing page
  const urlMaterial = searchParams.get('material');
  const urlSize = searchParams.get('size');
  const urlUrgency = searchParams.get('urgency');
  const urlDuoprop = searchParams.get('duoprop') === 'true';
  const urlServices = searchParams.get('services');
  const urlEstimate = searchParams.get('estimate');

  // Check if coming from pricing page
  const fromPricing = urlMaterial || urlSize;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propellerType: urlMaterial || '',
    isDuoprop: urlDuoprop,
    priority: urlUrgency ? (urgencyToPriority[urlUrgency] || 'green') : 'green',
    delivery: 'bring',
    message: '',
    website: '' // Honeypot - bots fill this, humans don't see it
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);

  // Image upload constants
  const MAX_IMAGES = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  // Anti-spam state
  const [formStartTime, setFormStartTime] = useState<number>(Date.now());
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);

  // Reset form start time when component mounts
  useEffect(() => {
    setFormStartTime(Date.now());

    // Pre-fill message if coming from pricing page
    if (fromPricing) {
      let prefilledMessage = 'Hinta-arviosta:\n';
      if (urlMaterial) prefilledMessage += `- Materiaali: ${urlMaterial}\n`;
      if (urlSize) prefilledMessage += `- Koko: ${urlSize}\n`;
      if (urlDuoprop) prefilledMessage += `- Duoprop: Kyllä\n`;
      if (urlUrgency) prefilledMessage += `- Aikataulu: ${urlUrgency}\n`;
      if (urlServices) prefilledMessage += `- Lisäpalvelut: ${urlServices}\n`;
      if (urlEstimate) prefilledMessage += `- Arvioitu hinta: ${urlEstimate}€\n`;
      prefilledMessage += '\nLisätiedot:\n';

      setFormData(prev => ({ ...prev, message: prefilledMessage }));
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    const files = Array.from(e.target.files || []);

    // Check total count
    if (selectedImages.length + files.length > MAX_IMAGES) {
      setImageError(`Voit lisätä enintään ${MAX_IMAGES} kuvaa`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setImageError('Vain kuvatiedostot sallittu');
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setImageError('Kuvan maksimikoko on 5 MB');
        continue;
      }
      validFiles.push(file);
    }

    setSelectedImages(prev => [...prev, ...validFiles]);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImageError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Anti-spam check 1: Honeypot - if filled, it's a bot
    if (formData.website) {
      // Fake success to not alert the bot
      setFormSubmitted(true);
      setTimeout(() => setFormSubmitted(false), 5000);
      return;
    }

    // Anti-spam check 2: Time-based - form filled too quickly
    const timeSpent = (Date.now() - formStartTime) / 1000;
    if (timeSpent < MIN_FORM_TIME_SECONDS) {
      setSubmitError('Lomake täytettiin liian nopeasti. Odota hetki ja yritä uudelleen.');
      return;
    }

    // Anti-spam check 3: Rate limiting
    if (lastSubmitTime && (Date.now() - lastSubmitTime) < RATE_LIMIT_MS) {
      const waitTime = Math.ceil((RATE_LIMIT_MS - (Date.now() - lastSubmitTime)) / 1000);
      setSubmitError(`Odota ${waitTime} sekuntia ennen seuraavaa lähetystä.`);
      return;
    }

    // Validation: either email OR phone must be provided
    if (!formData.email && !formData.phone) {
      setShowValidation(true);
      return;
    }

    setShowValidation(false);
    setIsSubmitting(true);

    try {
      // Build FormData for file upload support
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('propellerType', formData.propellerType);
      submitData.append('isDuoprop', formData.isDuoprop ? 'true' : 'false');
      submitData.append('priority', formData.priority);
      submitData.append('delivery', formData.delivery);
      submitData.append('message', formData.message);
      submitData.append('website', formData.website); // Honeypot

      // Add images
      selectedImages.forEach((image) => {
        submitData.append('images[]', image);
      });

      // Send to PHP backend
      const response = await fetch('/send-mail.php', {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send');
      }

      // Success
      setLastSubmitTime(Date.now());
      setFormSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        propellerType: '',
        isDuoprop: false,
        priority: 'green',
        delivery: 'bring',
        message: '',
        website: ''
      });
      setSelectedImages([]);
      setFormStartTime(Date.now());
      setTimeout(() => setFormSubmitted(false), 5000);
    } catch {
      setSubmitError('Viestin lähetys epäonnistui. Yritä uudelleen tai ota yhteyttä puhelimitse.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-cyan-900 via-blue-900 to-teal-900 py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-cyan-100 max-w-3xl">
            {t('contact.subtitle')}
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 via-gray-50/50 to-transparent pointer-events-none" />
      </div>

      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-8">{t('contact.info')}</h2>
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.phone')}</h3>
                    <a href="tel:+358400416466" className="text-cyan-600 hover:underline text-lg">
                      +358 400 416 466
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.email')}</h3>
                    <a href="mailto:info@potkurikorjaus.fi" className="text-cyan-600 hover:underline">
                      info@potkurikorjaus.fi
                    </a>
                    <p className="text-sm text-gray-500 mt-1">{t('contact.email.response')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.location')}</h3>
                    <p className="text-gray-700">Turuntie 3</p>
                    <p className="text-gray-700">37830 Akaa</p>
                    <p className="text-sm text-gray-500 mt-1">{t('contact.location.area')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.hours')}</h3>
                    <p className="text-gray-700">{t('contact.hours.weekday')}</p>
                    <p className="text-gray-700">{t('contact.hours.weekend')}</p>
                  </div>
                </div>

                {/* Active Holiday Notice */}
                {activeHoliday && activeHoliday.type === 'closed' && (
                  <div className="flex items-start gap-4 bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CalendarOff className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-red-700 mb-1">
                        {language === 'fi' ? 'Suljettu' : language === 'sv' ? 'Stängd' : 'Closed'}
                      </h3>
                      <p className="text-red-700 font-medium">{activeHoliday.name_fi || activeHoliday.name}</p>
                      <p className="text-sm text-red-600">
                        {activeHoliday.end_date && activeHoliday.end_date !== activeHoliday.date
                          ? `${activeHoliday.date} - ${activeHoliday.end_date}`
                          : activeHoliday.date
                        }
                      </p>
                    </div>
                  </div>
                )}

                {/* Upcoming Holidays */}
                {upcomingHolidays.length > 0 && !activeHoliday && (
                  <div className="flex items-start gap-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CalendarOff className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-amber-700 mb-2">
                        {language === 'fi' ? 'Tulevat poikkeuspäivät' : language === 'sv' ? 'Kommande helgdagar' : 'Upcoming holidays'}
                      </h3>
                      <ul className="space-y-1">
                        {upcomingHolidays.slice(0, 3).map((holiday, index) => (
                          <li key={index} className="text-sm text-amber-700">
                            <span className="font-medium">{holiday.name_fi || holiday.name}</span>
                            <span className="text-amber-600 ml-2">
                              {holiday.end_date && holiday.end_date !== holiday.date
                                ? `${holiday.date} - ${holiday.end_date}`
                                : holiday.date
                              }
                            </span>
                            {holiday.type === 'closed' && (
                              <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                {language === 'fi' ? 'Suljettu' : language === 'sv' ? 'Stängd' : 'Closed'}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-xl">
                <MessageSquare className="w-8 h-8 text-cyan-600 mb-3" />
                <h3 className="font-bold text-lg mb-2">{t('contact.questions')}</h3>
                <p className="text-gray-700 mb-4">
                  {t('contact.questions.desc')}
                </p>
                <a
                  href="tel:+358400416466"
                  className="inline-block bg-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-cyan-700 transition-colors"
                >
                  {t('common.callNow')}
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div id="lomake" className="bg-white p-8 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-bold mb-6">{t('contact.form.title')}</h2>

              {/* Estimate Banner from Pricing Page */}
              {fromPricing && urlEstimate && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Calculator className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{t('contact.form.estimate')}</p>
                      <p className="text-2xl font-bold text-cyan-600">{urlEstimate}€</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('contact.form.prefilled')}
                  </p>
                </div>
              )}

              {showValidation && !formData.email && !formData.phone && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">
                    {t('contact.form.validation.contact')}
                  </p>
                </div>
              )}

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users, bots will fill it */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="absolute -left-[9999px] opacity-0"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.name')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.email')} {!formData.phone && '*'}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required={!formData.phone}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.phone')} {!formData.email && '*'}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required={!formData.email}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="+358 40 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.propellerType')} *
                  </label>
                  <select
                    name="propellerType"
                    required
                    value={formData.propellerType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    <option value="">{t('contact.form.propellerType.select')}</option>
                    <option value="Alumiini">{t('contact.form.propellerType.aluminum')}</option>
                    <option value="Ruostumaton teräs">{t('contact.form.propellerType.stainless')}</option>
                    <option value="Pronssi">{t('contact.form.propellerType.bronze')}</option>
                    <option value="Muu potkurityyppi">{t('contact.form.propellerType.other')}</option>
                  </select>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isDuoprop"
                      checked={formData.isDuoprop}
                      onChange={(e) => setFormData({ ...formData, isDuoprop: e.target.checked })}
                      className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-700">{t('contact.form.duoprop')}</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.priority')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'green', labelKey: 'contact.form.priority.normal', descKey: 'contact.form.priority.normal.desc', bg: 'bg-green-50', border: 'border-green-400', selected: 'ring-green-500' },
                      { value: 'yellow', labelKey: 'contact.form.priority.moderate', descKey: 'contact.form.priority.moderate.desc', bg: 'bg-yellow-50', border: 'border-yellow-400', selected: 'ring-yellow-500' },
                      { value: 'orange', labelKey: 'contact.form.priority.urgent', descKey: 'contact.form.priority.urgent.desc', bg: 'bg-orange-50', border: 'border-orange-400', selected: 'ring-orange-500' },
                      { value: 'red', labelKey: 'contact.form.priority.emergency', descKey: 'contact.form.priority.emergency.desc', bg: 'bg-red-50', border: 'border-red-400', selected: 'ring-red-500' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, priority: option.value })}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${option.bg} ${option.border} ${
                          formData.priority === option.value ? `ring-2 ring-offset-1 ${option.selected}` : 'opacity-70 hover:opacity-100'
                        }`}
                      >
                        <div className="font-semibold text-gray-900 text-sm">{t(option.labelKey)}</div>
                        <div className="text-xs text-gray-600">{t(option.descKey)}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.delivery')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="delivery"
                        value="bring"
                        checked={formData.delivery === 'bring'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>{t('contact.form.delivery.bring')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="delivery"
                        value="send"
                        checked={formData.delivery === 'send'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>{t('contact.form.delivery.send')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="delivery"
                        value="pickup"
                        checked={formData.delivery === 'pickup'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>{t('contact.form.delivery.pickup')}</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('contact.form.message.placeholder')}
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.images')}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    {t('contact.form.images.desc')}
                  </p>

                  {/* Image previews */}
                  {selectedImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {selectedImages.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Kuva ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload button */}
                  {selectedImages.length < MAX_IMAGES && (
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-colors">
                      <Camera className="w-5 h-5 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {selectedImages.length === 0 ? t('contact.form.images.add') : t('contact.form.images.addMore')}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </label>
                  )}

                  {imageError && (
                    <p className="text-sm text-red-600 mt-2">{imageError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                    isSubmitting
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:shadow-xl'
                  }`}
                >
                  <Send className="w-5 h-5" />
                  {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                </button>

                {formSubmitted && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-green-700">
                      {t('contact.form.success')}
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Google Maps */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8">{t('contact.map')}</h2>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1937.6684444444445!2d23.8624!3d61.1667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x468edf1234567890%3A0x1234567890abcdef!2sTuruntie%203%2C%2037830%20Akaa!5e0!3m2!1sen!2sfi!4v1234567890123!5m2!1sen!2sfi"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Potkurikorjaus sijainti kartalla"
            />
          </div>
          <p className="text-center text-gray-600 mt-4">
            Turuntie 3, 37830 Akaa
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
